import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  untrack,
} from 'solid-js';
import { SharedCapabilityProviderBase } from '../base/shared-provider-base';
import {
  POINT_CLOUD_STYLING_DEFAULTS,
  PointCloudStyling,
  PointCloudClassDefinition,
  PointCloudClassification,
  DEFAULT_CLASSIFICATION_COLOR,
  ClassStatistics,
} from '@trimble/types';

const HEX_COLOR_LENGTH = 6; // RRGGBB format

type ClassOverride = Partial<
  Pick<PointCloudClassDefinition, 'color' | 'visibility'>
>;

export class PointCloudStylingCapabilityProvider extends SharedCapabilityProviderBase<PointCloudStyling> {
  public readonly idKey = 'stylingType' as const;

  public readonly pointCloudStyling: Accessor<PointCloudStyling>;

  public readonly classifications: Accessor<PointCloudClassification[]>;

  private readonly userClassOverrides: Accessor<Map<number, ClassOverride>>;
  private readonly setUserClassOverrides: (
    value: Map<number, ClassOverride>
  ) => void;

  private isInternalUpdate = false;

  constructor(
    private readonly classificationSchemes?: Accessor<
      PointCloudClassification[]
    >,
    private readonly classStatistics?: Accessor<ClassStatistics>,
    public readonly defaultStyling: Accessor<PointCloudStyling> = () =>
      POINT_CLOUD_STYLING_DEFAULTS
  ) {
    super();

    const [userClassOverrides, setUserClassOverrides] = createSignal<
      Map<number, ClassOverride>
    >(new Map());
    this.userClassOverrides = userClassOverrides;
    this.setUserClassOverrides = setUserClassOverrides;

    this.classifications = createMemo((): PointCloudClassification[] => {
      const schemes = this.classificationSchemes?.() ?? [];
      const classStatistics = this.classStatistics?.() ?? {};
      const overrides = this.userClassOverrides();

      const classifications = this.getClassifications(schemes, classStatistics);

      return classifications.map((classification) => {
        const baseColor =
          '#' + this.removeAlpha(classification.color).toLowerCase();
        const override = overrides.get(classification.id);
        return {
          ...classification,
          color: override?.color ?? baseColor,
          visible: override?.visibility ?? classification.visible,
        };
      });
    });

    this.pointCloudStyling = createMemo(() => {
      const items = this.items();
      return items.length === 0 ? this.defaultStyling() : items[0];
    });

    createEffect(
      on(this.classifications, (classifications) => {
        const classDefinitions = classifications.map((classification) => ({
          id: classification.id,
          color: classification.color,
          visibility: classification.visible,
        }));

        const pointCloudStyling = this.pointCloudStyling();

        this.isInternalUpdate = true;
        this.setPointCloudStyling({
          ...pointCloudStyling,
          classification: {
            classDefinitions,
          },
        });
        this.isInternalUpdate = false;
      })
    );

    this.setPointCloudStyling(untrack(this.defaultStyling));
  }

  public override cleanup(): Promise<void> {
    return Promise.resolve(undefined);
  }

  public setPointCloudStyling(pointCloudStyling: PointCloudStyling): void {
    if (!this.isInternalUpdate) {
      untrack(() => this.trackUserOverrides(pointCloudStyling));
    }

    this.setItems([{ ...pointCloudStyling }]);
  }

  public clearUserClassOverrides(): void {
    this.setUserClassOverrides(new Map());
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 26 > 15 allowed; refactor later
  private trackUserOverrides(styling: PointCloudStyling): void {
    const incomingDefs = styling.classification?.classDefinitions;
    if (!incomingDefs || incomingDefs.length === 0) return;

    const rawSchemes = this.classificationSchemes?.() ?? [];
    if (rawSchemes.length === 0) return;

    const schemeByClassId = new Map(
      rawSchemes.map((s) => [
        s.id,
        {
          color: '#' + this.removeAlpha(s.color).toLowerCase(),
          visible: s.visible,
        },
      ])
    );

    const newOverrides = new Map(this.userClassOverrides());
    let changed = false;

    for (const def of incomingDefs) {
      const scheme = schemeByClassId.get(def.id);
      if (!scheme) continue;

      const normalizedColor = def.color?.toLowerCase();
      const colorDiffers = normalizedColor !== scheme.color;
      const visibilityDiffers = def.visibility !== scheme.visible;

      if (colorDiffers || visibilityDiffers) {
        const existing = newOverrides.get(def.id);
        const newColor = colorDiffers ? normalizedColor : undefined;
        const newVisibility = visibilityDiffers ? def.visibility : undefined;

        if (
          existing?.color !== newColor ||
          existing?.visibility !== newVisibility
        ) {
          const override: ClassOverride = {};
          if (colorDiffers) override.color = normalizedColor;
          if (visibilityDiffers) override.visibility = def.visibility;
          newOverrides.set(def.id, override);
          changed = true;
        }
      } else if (newOverrides.has(def.id)) {
        newOverrides.delete(def.id);
        changed = true;
      }
    }

    if (changed) {
      this.setUserClassOverrides(newOverrides);
    }
  }

  private removeAlpha(color: string): string {
    return color.substring(0, HEX_COLOR_LENGTH);
  }

  /**
   * Returns the classifications with the percentage of the class statistics
   * If no class statistics are available, returns the schemes with 0% percentage
   */
  private getClassifications(
    schemes: PointCloudClassification[],
    classStatistics: { [key: number]: number }
  ): PointCloudClassification[] {
    const hasClassStatistics = Object.keys(classStatistics).length > 0;
    // If no class statistics are available, return the schemes with 0% percentage
    if (!hasClassStatistics) {
      return schemes.map((scheme) => ({
        ...scheme,
        percentage: 0,
      }));
    }

    const total = Object.values(classStatistics).reduce(
      (acc, value) => acc + value,
      0
    );

    // Calculate percentages for each classification
    const percentages: { id: number; percentage: number }[] =
      total > 0
        ? Object.entries(classStatistics).reduce(
            (acc, [id, value]) => {
              return [
                ...acc,
                {
                  id: Number(id),
                  // eslint-disable-next-line no-magic-numbers
                  percentage: Math.round((value / total) * 100),
                },
              ];
            },
            [] as { id: number; percentage: number }[]
          )
        : [];

    const classifications = percentages.map((percentage) => {
      const scheme = schemes.find((scheme) => scheme.id === percentage.id) ?? {
        id: percentage.id,
        label: `Class ${percentage.id}`,
        visible: true,
        percentage: percentage.percentage,
        color: DEFAULT_CLASSIFICATION_COLOR,
      };

      return {
        ...scheme,
        percentage: percentage.percentage,
      };
    });

    return classifications;
  }
}
